
import { useState } from "react";
import { Helmet } from "react-helmet";
import { PortfolioHeader } from "@/components/portfolio/PortfolioHeader";
import { PortfolioEditor } from "@/components/portfolio/PortfolioEditor";
import { PortfolioPreview } from "@/components/portfolio/PortfolioPreview";
import { PortfolioTemplates } from "@/components/portfolio/PortfolioTemplates";
import { PortfolioSidebar } from "@/components/portfolio/PortfolioSidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";

export interface PortfolioItem {
  id: string;
  type: "image" | "text" | "video" | "project";
  content: string;
  title?: string;
  description?: string;
  tags?: string[];
  link?: string;
}

export interface PortfolioData {
  id: string;
  title: string;
  description: string;
  coverImage?: string;
  items: PortfolioItem[];
  template: string;
  color: string;
  font: string;
  visibility: "public" | "private" | "unlisted";
}

const DigitalPortfolio = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("editor");
  const [portfolio, setPortfolio] = useState<PortfolioData>({
    id: "portfolio-" + Date.now(),
    title: "My Portfolio",
    description: "A showcase of my best work",
    items: [],
    template: "minimal",
    color: "#8B5CF6", // Vivid purple
    font: "Inter",
    visibility: "private"
  });

  const handleSave = () => {
    // In a real app, this would save to backend
    localStorage.setItem(`portfolio-${portfolio.id}`, JSON.stringify(portfolio));
    toast({
      title: "Portfolio saved",
      description: "Your portfolio has been saved successfully.",
    });
  };

  const updatePortfolio = (updates: Partial<PortfolioData>) => {
    setPortfolio(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="container max-w-7xl mx-auto py-6 px-4 md:px-6">
      <Helmet>
        <title>Digital Portfolio Builder | DGK Hub</title>
      </Helmet>

      <PortfolioHeader 
        portfolio={portfolio} 
        updatePortfolio={updatePortfolio}
        onSave={handleSave}
      />

      <div className="flex flex-col lg:flex-row gap-4 mt-6">
        {/* Left Sidebar with tools */}
        <div className="lg:w-64 flex-shrink-0">
          <PortfolioSidebar 
            portfolio={portfolio} 
            updatePortfolio={updatePortfolio}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <Tabs 
            defaultValue="editor" 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="w-full border-b rounded-none p-0 h-auto">
              <TabsTrigger 
                value="editor" 
                className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
              >
                Editor
              </TabsTrigger>
              <TabsTrigger 
                value="templates" 
                className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
              >
                Templates
              </TabsTrigger>
              <TabsTrigger 
                value="preview" 
                className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
              >
                Preview
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="editor" className="mt-0 px-0">
              <PortfolioEditor 
                portfolio={portfolio}
                updatePortfolio={updatePortfolio}
              />
            </TabsContent>
            
            <TabsContent value="templates" className="mt-0 px-0">
              <PortfolioTemplates 
                currentTemplate={portfolio.template}
                onSelectTemplate={(template) => updatePortfolio({ template })}
              />
            </TabsContent>
            
            <TabsContent value="preview" className="mt-0 px-0">
              <PortfolioPreview portfolio={portfolio} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default DigitalPortfolio;
